import { FC, useContext, useState } from "react";
import {
  Button,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  AddCircle as AddCircleIcon
} from "@mui/icons-material";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { ConfirmationDialog } from "components/ConfirmationDialog";

import { CaseLink } from "interfaces/case-links";
import { useCaseLinksQuery } from "hooks/queries/case-links";
import { CreateCaseLinkDialog } from "components/CaseManagement/CaseDetail/CaseLinks/Dialogs/CreateCaseLinkDialog";
import { EditCaseLinkDialog } from "components/CaseManagement/CaseDetail/CaseLinks/Dialogs/EditCaseLinkDialog";

interface Props {
  caseId: string;
}

export const CaseLinks: FC<Props> = ({ caseId }): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const queryClient = useQueryClient();

  const {
    data: caseLinks,
    isLoading: isCaseLinksLoading,
    isError: isCaseLinksError
  } = useCaseLinksQuery({ case: caseId });

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [linkDeleteId, setLinkDeleteId] = useState<number | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [linkToEdit, setLinkToEdit] = useState<CaseLink | null>(null);

  const createCaseLinkMutation = useMutation({
    mutationFn: async (values: {
      name: string;
      url: string;
    }): Promise<void> => {
      return axios.post("/case_links", {
        case: caseId,
        ...values
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(["case", "links", { case: caseId }]);
      setShowCreateDialog(false);
      addSnackbarToQueue({
        message: "Case link created successfully.",
        severity: "success"
      });
    },
    onError: () => {
      addSnackbarToQueue({
        message: "Failed to create case link.",
        severity: "error"
      });
    }
  });

  const editCaseLinkMutation = useMutation({
    mutationFn: async (values: {
      id: number;
      name: string;
      url: string;
    }): Promise<void> => {
      return axios.patch(`/case_links/${values.id}`, {
        case: caseId,
        ...values
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(["case", "links", { case: caseId }]);
      setShowEditDialog(false);
      addSnackbarToQueue({
        message: "Case link updated successfully.",
        severity: "success"
      });
    },
    onError: () => {
      addSnackbarToQueue({
        message: "Failed to update case link.",
        severity: "error"
      });
    }
  });

  const deleteCaseLinkMutation = useMutation({
    mutationFn: async (id: number): Promise<void> => {
      return axios.delete(`/case_links/${id}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(["case", "links", { case: caseId }]);
      addSnackbarToQueue({
        message: "Case link deleted successfully.",
        severity: "success"
      });
    },
    onError: () => {
      addSnackbarToQueue({
        message: "Failed to delete case link.",
        severity: "error"
      });
    }
  });

  const handleCreate = (): void => {
    setShowCreateDialog(true);
  };

  const handleEdit = (link: CaseLink): void => {
    setLinkToEdit(link);
    setShowEditDialog(true);
  };

  const handleDelete = (id: number): void => {
    if (!id) {
      return;
    }
    setLinkDeleteId(id);
    setShowDeleteDialog(true);
  };

  return (
    <>
      <ProgressIndicatorContainer
        loading={isCaseLinksLoading}
        error={isCaseLinksError}
        errorMessage="Failed to load case links..."
      >
        {!caseLinks?.count && (
          <Typography textAlign="center" variant="body2" pb={2}>
            No links yet.
          </Typography>
        )}
        <Stack gap={2}>
          {caseLinks?.results.map(link => (
            <Stack
              key={link.id}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Link href={link.url} target="_blank">
                {link.name || link.url}
              </Link>

              <Stack direction="row" gap={1}>
                <Tooltip title="Edit link">
                  <IconButton onClick={() => handleEdit(link)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Delete link">
                  <IconButton onClick={() => handleDelete(link.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          ))}

          <Stack direction="row">
            <Button
              variant="outlined"
              onClick={handleCreate}
              data-qa="CM-Button-Add-Link"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <AddCircleIcon fontSize="small" /> Add link
            </Button>
          </Stack>
        </Stack>
      </ProgressIndicatorContainer>

      {showCreateDialog && (
        <CreateCaseLinkDialog
          isOpen={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          onConfirm={async values => {
            await createCaseLinkMutation.mutate(values);
          }}
        />
      )}

      {showEditDialog && linkToEdit && (
        <EditCaseLinkDialog
          link={linkToEdit}
          isOpen={showEditDialog}
          onClose={() => setShowEditDialog(false)}
          onConfirm={async values => {
            await editCaseLinkMutation.mutate(values);
          }}
        />
      )}

      {showDeleteDialog && linkDeleteId && (
        <ConfirmationDialog
          title="Are you sure you want to delete this link?"
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={async () => {
            if (linkDeleteId) {
              await deleteCaseLinkMutation.mutate(linkDeleteId);
              setLinkDeleteId(null);
            }
          }}
        />
      )}
    </>
  );
};
